import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useShareLink
 * Encapsulates all share link management logic.
 * Components receive data + actions — no API code needed in the modal.
 */
export function useShareLink(cardId, open) {
  const [shareData, setShareData] = useState(null);
  const [accessLevel, setAccessLevel] = useState('collaborate');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [copied, setCopied] = useState(false);

  // Load existing share info when modal opens
  useEffect(() => {
    if (!open || !cardId) return;
    setLoading(true);
    // Cookies sent automatically
    axios.get(`${API_BASE}/action-cards/${cardId}/share`)
      .then(res => {
        if (res.data?.token) {
          setShareData(res.data);
          const p = res.data.permissions || {};
          setAccessLevel(p.level || (p.can_act === false ? 'comment' : 'collaborate'));
        } else {
          setShareData(null);
        }
      })
      .catch(err => console.error('Failed to load share info:', err))
      .finally(() => setLoading(false));
  }, [open, cardId]);

  const generate = useCallback(async (level = accessLevel) => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API_BASE}/action-cards/${cardId}/share`, { access_level: level });
      setShareData(res.data);
      toast.success('Share link generated!');
    } catch (err) {
      console.error('Share generation failed:', err);
      toast.error('Failed to generate share link');
    } finally {
      setGenerating(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, accessLevel]);

  const revoke = useCallback(async () => {
    if (!window.confirm('Revoke this link? Anyone with it will lose access.')) return;
    setRevoking(true);
    try {
      await axios.delete(`${API_BASE}/action-cards/${cardId}/share`);
      setShareData(null);
      toast.success('Share link revoked');
    } catch (err) {
      console.error('Revoke failed:', err);
      toast.error('Failed to revoke');
    } finally {
      setRevoking(false);
    }
  }, [cardId]);

  const copy = useCallback(async (url) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for browsers that block clipboard API
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2500);
  }, []);

  const updateAccess = useCallback(async (level) => {
    setAccessLevel(level);
    if (shareData) {
      try {
        const res = await axios.post(`${API_BASE}/action-cards/${cardId}/share`, { access_level: level });
        setShareData(res.data);
      } catch (err) {
        console.error('Permission update failed:', err);
        toast.error('Failed to update permissions');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, shareData]);

  return {
    shareData,
    accessLevel,
    loading,
    generating,
    revoking,
    copied,
    generate,
    revoke,
    copy,
    updateAccess,
  };
}
